import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck, MessageSquareText, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CurrentUser } from "../lib/auth";
import { api } from "../lib/api";
import { useDialog } from "../hooks/useDialog";
import { ModalPortal } from "./ModalPortal";

const guideRoles = ["manager", "administrator", "concierge"];
const storageKey = (userId: string) => `qy-shift-guide:${userId}`;

const steps: ReadonlyArray<[string, string]> = [
  ["Сверка денег", "Вместе с прошлой сменой пересчитайте наличные в кассе и сравните с суммой в CRM."],
  ["Заселения", "Посмотрите, кто заезжает сегодня: время, апартамент, оплата, готовы ли ключ-карты."],
  ["Выезды гостей", "Проверьте выезды на сегодня и не остались ли незакрытые выезды с прошлой смены."],
  ["Уборка", "Откройте «Уборку»: что в работе, что ждёт горничную, нет ли просроченных задач."],
  ["Другое", "Прочитайте комментарий прошлой смены: просьбы гостей, поломки, поручения."],
];

/** Подсказка при входе: что проверить до приёма смены. Смена сама не начинается. */
export function ShiftStartGuide({ user }: { user: CurrentUser }) {
  const eligible = guideRoles.includes(user.role);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.sessionStorage.getItem(storageKey(user.id)) === "1"; } catch { return false; }
  });
  const shift = useQuery({ queryKey: ["operational-shift"], queryFn: api.currentOperationalShift, enabled: eligible && !dismissed });
  const reports = useQuery({ queryKey: ["operational-shift-reports"], queryFn: api.operationalShiftReports, enabled: eligible && !dismissed });
  const navigate = useNavigate();
  const close = () => {
    try { window.sessionStorage.setItem(storageKey(user.id), "1"); } catch { /* приватный режим */ }
    setDismissed(true);
  };
  if (!eligible || dismissed || shift.isPending || shift.isError) return null;
  if (shift.data?.acceptedById === user.id) return null;
  const active = shift.data;
  const lastReport = reports.data?.[0];
  const lastNotes = [lastReport?.notes, lastReport?.handoverChecklist?.extraNotes].filter(Boolean).join("\n");
  return <ShiftStartGuideDialog user={user} activeBy={active?.acceptedBy} lastBy={lastReport?.acceptedBy} lastNotes={lastNotes}
    onClose={close} onStart={() => { close(); navigate("/shift"); }}/>;
}

function ShiftStartGuideDialog({ user, activeBy, lastBy, lastNotes, onClose, onStart }: {
  user: CurrentUser; activeBy?: string; lastBy?: string; lastNotes: string; onClose: () => void; onStart: () => void;
}) {
  const ref = useDialog<HTMLElement>(onClose);
  return <ModalPortal onClose={onClose}>
    <section ref={ref} className="booking-modal shift-guide" role="dialog" aria-modal="true" aria-labelledby="shift-guide-title">
      <header className="modal-header">
        <div><h2 id="shift-guide-title">{user.name.split(/\s+/)[0]}, перед началом работы</h2><p>Проверьте пять пунктов. Смена начнётся, только когда вы примете её вручную.</p></div>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть"><X size={17}/></button>
      </header>
      <div className="modal-body">
        <ol className="shift-guide__steps">
          {steps.map(([title, text]) => <li key={title}><strong>{title}</strong><span>{text}</span></li>)}
        </ol>
        {lastNotes && <div className="shift-guide__notes"><MessageSquareText size={16}/><div><strong>Комментарий прошлой смены{lastBy ? ` · ${lastBy}` : ""}</strong><p>{lastNotes}</p></div></div>}
        {activeBy && <div className="form-notice is-warning"><ClipboardCheck size={16}/><span><strong>Смену сейчас ведёт {activeBy}</strong>Примите смену после того, как {activeBy} её сдаст.</span></div>}
      </div>
      <footer className="modal-footer">
        <button type="button" className="secondary-button" onClick={onClose}>Позже</button>
        <button type="button" className="primary-button" onClick={onStart}>{activeBy ? "Открыть «Смену»" : "Перейти к приёму смены"}</button>
      </footer>
    </section>
  </ModalPortal>;
}

export function forgetShiftStartGuide() {
  try {
    for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith("qy-shift-guide:")) window.sessionStorage.removeItem(key);
    }
  } catch { /* приватный режим */ }
}
